import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle } from 'lucide-react';

const OrderConfirmationScreen = ({ 
  orderDetails, 
  usdtToCnyRate, 
  tg, 
  setCurrentScreen, 
  setOrderDetails,
  apiUrl,
  makeAuthenticatedRequest 
}) => {
  const [liveOrderData, setLiveOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Логирование полученных пропсов
  useEffect(() => {
    console.log('=== OrderConfirmationScreen Props ===');
    console.log('orderDetails:', orderDetails);
    console.log('orderDetails type:', typeof orderDetails);
    console.log('orderDetails keys:', orderDetails ? Object.keys(orderDetails) : 'null');
    console.log('orderDetails.orderId:', orderDetails?.orderId);
    console.log('orderDetails.order_id:', orderDetails?.order_id);
    console.log('orderDetails.id:', orderDetails?.id);
    console.log('apiUrl:', apiUrl);
    console.log('makeAuthenticatedRequest:', typeof makeAuthenticatedRequest);
  }, [orderDetails, apiUrl, makeAuthenticatedRequest]);

  // Функция для получения данных заказа с сервера
  const fetchOrderData = useCallback(async () => {
    console.log('fetchOrderData вызван');
    
    // Поддерживаем разные варианты полей
    const actualOrderId = orderDetails?.orderId || orderDetails?.order_id || orderDetails?.id;
    
    console.log('orderDetails?.orderId:', orderDetails?.orderId);
    console.log('orderDetails?.order_id:', orderDetails?.order_id);
    console.log('orderDetails?.id:', orderDetails?.id);
    console.log('actualOrderId:', actualOrderId);
    console.log('makeAuthenticatedRequest exists:', !!makeAuthenticatedRequest);
    console.log('apiUrl:', apiUrl);
    
    if (!actualOrderId) {
      console.warn('⚠️ Нет orderId (проверили orderId, order_id, id), пропускаем загрузку');
      console.warn('orderDetails полностью:', JSON.stringify(orderDetails));
      setIsLoading(false);
      return;
    }
    
    if (!makeAuthenticatedRequest) {
      console.warn('⚠️ Нет makeAuthenticatedRequest, пропускаем загрузку');
      setIsLoading(false);
      return;
    }
    
    if (!apiUrl) {
      console.warn('⚠️ Нет apiUrl, пропускаем загрузку');
      setIsLoading(false);
      return;
    }

    try {
      console.log('📥 Загрузка данных заказа:', actualOrderId);
      
      const response = await makeAuthenticatedRequest(`${apiUrl}/api/order/${actualOrderId}`);
      
      console.log('Response status:', response.status);
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Сервер вернул не JSON:', text.substring(0, 200));
        throw new Error('Сервер вернул некорректный ответ');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Данные заказа получены:', data);
      console.log('🔍 data.payment_details:', data.payment_details);
      console.log('🔍 data.paymentDetails:', data.paymentDetails);
      console.log('🔍 data.status:', data.status);
      console.log('🔍 data.paymentMethod:', data.paymentMethod);
      
      setLiveOrderData(data);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      console.error('❌ Ошибка загрузки данных заказа:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [orderDetails?.orderId, orderDetails?.order_id, orderDetails?.id, orderDetails, apiUrl, makeAuthenticatedRequest]);

  // Первая загрузка при монтировании
  useEffect(() => {
    console.log('🚀 useEffect монтирования вызван');
    console.log('fetchOrderData:', typeof fetchOrderData);
    fetchOrderData();
  }, [fetchOrderData]);

  // Polling: автоматическое обновление каждые 5 секунд
  useEffect(() => {
    // Не запускаем polling если нет данных или заказ в финальном статусе
    const shouldPoll = liveOrderData && 
                       liveOrderData.status !== 'completed' && 
                       liveOrderData.status !== 'cancelled';

    if (!shouldPoll) {
      console.log('⏹️ Polling остановлен (финальный статус или нет данных)');
      return;
    }

    const pollingOrderId = orderDetails?.orderId || orderDetails?.order_id || orderDetails?.id;
    console.log('🔄 Запуск polling для заказа:', pollingOrderId);
    
    const interval = setInterval(() => {
      fetchOrderData();
    }, 5000); // Каждые 5 секунд

    // Очистка при размонтировании
    return () => {
      console.log('🛑 Polling остановлен');
      clearInterval(interval);
    };
  }, [liveOrderData?.status, orderDetails?.orderId, orderDetails?.order_id, orderDetails?.id, fetchOrderData]);

  // Используем данные с сервера если доступны, иначе переданные orderDetails
  const displayData = liveOrderData || orderDetails;

  if (!displayData) return null;

  if (isLoading && !liveOrderData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 p-4 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Загрузка заявки...</p>
        </div>
      </div>
    );
  }

  if (error && !liveOrderData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">⚠️ Ошибка загрузки заявки</div>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => setCurrentScreen('main')}
            className="bg-gray-700 text-white px-6 py-3 rounded-xl hover:bg-gray-600 transition-all"
          >
            Вернуться на главную
          </button>
        </div>
      </div>
    );
  }

  const isBank = displayData.paymentMethod === 'RUB';
  const isTRC20 = displayData.network === 'TRC-20';
  const isBybit = displayData.network === 'Bybit';
  
  // Поддерживаем оба варианта: payment_details и paymentDetails
  const actualPaymentDetails = displayData.payment_details || displayData.paymentDetails;
  const hasPaymentDetails = actualPaymentDetails && actualPaymentDetails.trim() !== '';
  
  console.log('🔍 OrderConfirmation display check:');
  console.log('  displayData.paymentMethod:', displayData.paymentMethod);
  console.log('  displayData.payment_details:', displayData.payment_details);
  console.log('  displayData.paymentDetails:', displayData.paymentDetails);
  console.log('  actualPaymentDetails:', actualPaymentDetails);
  console.log('  isBank:', isBank);
  console.log('  hasPaymentDetails:', hasPaymentDetails);
  console.log('  displayData.status:', displayData.status);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    if (tg) {
      tg.showAlert(`${label} скопирован!`);
    } else {
      alert(`${label} скопирован!`);
    }
  };

  // Проверяем, завершена ли заявка
  const isCompleted = displayData.status === 'completed';
  const isCancelled = displayData.status === 'cancelled';

  // Если заявка завершена - показываем специальную страницу БЕЗ реквизитов
  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 p-4">
        <div className="max-w-md mx-auto pt-8">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl p-8 border border-gray-700">
            {/* Иконка успешного завершения */}
            <div className="text-center mb-6">
              <div className="inline-block bg-green-900/50 rounded-full p-6 mb-4 border-2 border-green-500 relative">
                <svg className="w-16 h-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Заявка выполнена!</h2>
              <p className="text-gray-400">№ {displayData.orderId}</p>
            </div>

            {/* Статус завершения */}
            <div className="mb-6 text-center">
              <span className="inline-block px-6 py-3 rounded-full text-sm font-semibold bg-green-900/50 text-green-300 border-2 border-green-500">
                ✓ Завершено
              </span>
            </div>

            {/* Детали заявки */}
            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-2xl p-5 mb-6 border border-green-800/30">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Тип:</span>
                  <span className="font-semibold text-white">{displayData.type === 'alipay' ? 'Alipay' : displayData.type === 'wechat' ? 'WeChat' : displayData.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Сумма:</span>
                  <span className="font-semibold text-green-400">{displayData.amount} CNY</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Способ оплаты:</span>
                  <span className="font-semibold text-white">{displayData.paymentMethod}</span>
                </div>
                {displayData.bank && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Банк:</span>
                    <span className="font-semibold text-white">{displayData.bank}</span>
                  </div>
                )}
                {displayData.network && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Сеть:</span>
                    <span className="font-semibold text-white">{displayData.network}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Сообщение о завершении */}
            <div className="bg-green-900/30 border-2 border-green-700/50 rounded-2xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <div className="bg-green-800/50 rounded-full p-2 mt-1">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-green-300 mb-2">Операция завершена успешно!</h3>
                  <p className="text-sm text-green-400 leading-relaxed">
                    Средства зачислены на ваш {displayData.type === 'alipay' ? 'Alipay' : displayData.type === 'wechat' ? 'WeChat' : displayData.type} аккаунт. 
                    Спасибо за использование нашего сервиса!
                  </p>
                </div>
              </div>
            </div>

            {/* Кнопки для завершенной заявки */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setCurrentScreen('profile');
                  setOrderDetails(null);
                }}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl py-4 font-semibold shadow-lg hover:shadow-xl hover:from-green-500 hover:to-emerald-500 transition-all"
              >
                Вернуться в профиль
              </button>
              <button
                onClick={() => {
                  setCurrentScreen('main');
                  setOrderDetails(null);
                }}
                className="w-full bg-gray-700 text-gray-200 rounded-xl py-4 font-semibold hover:bg-gray-600 transition-all border border-gray-600"
              >
                На главную
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Если заявка отменена - показываем специальную страницу
  if (isCancelled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 p-4">
        <div className="max-w-md mx-auto pt-8">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl p-8 border border-gray-700">
            {/* Иконка отмены */}
            <div className="text-center mb-6">
              <div className="inline-block bg-red-900/50 rounded-full p-6 mb-4 border-2 border-red-700">
                <svg className="w-16 h-16 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Заявка отменена</h2>
              <p className="text-gray-400">№ {displayData.orderId}</p>
            </div>

            {/* Статус отмены */}
            <div className="mb-6 text-center">
              <span className="inline-block px-6 py-3 rounded-full text-sm font-semibold bg-red-900/50 text-red-300 border-2 border-red-700">
                ✗ Отменено
              </span>
            </div>

            {/* Детали заявки */}
            <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-2xl p-5 mb-6 border border-red-800/30">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Тип:</span>
                  <span className="font-semibold text-white">{displayData.type === 'alipay' ? 'Alipay' : displayData.type === 'wechat' ? 'WeChat' : displayData.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Сумма:</span>
                  <span className="font-semibold text-white">{displayData.amount} CNY</span>
                </div>
              </div>
            </div>

            {/* Сообщение об отмене */}
            <div className="bg-red-900/30 border-2 border-red-700/50 rounded-2xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <div className="bg-red-800/50 rounded-full p-2 mt-1">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-red-300 mb-2">Заявка была отменена</h3>
                  <p className="text-sm text-red-400 leading-relaxed">
                    Если у вас есть вопросы, пожалуйста, свяжитесь с поддержкой.
                  </p>
                </div>
              </div>
            </div>

            {/* Кнопки */}
            <div className="space-y-3">
              <button
                onClick={() => setCurrentScreen('support')}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl py-4 font-semibold shadow-lg hover:shadow-xl hover:from-indigo-500 hover:to-purple-500 transition-all flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Связаться с поддержкой
              </button>
              <button
                onClick={() => {
                  setCurrentScreen('main');
                  setOrderDetails(null);
                }}
                className="w-full bg-gray-700 text-gray-200 rounded-xl py-4 font-semibold hover:bg-gray-600 transition-all border border-gray-600"
              >
                Вернуться на главную
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Для активных заявок (pending, processing) - показываем реквизиты
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 p-4">
      <div className="max-w-md mx-auto pt-8">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl shadow-2xl p-8 border border-gray-700">
          {/* Иконка успеха создания */}
          <div className="text-center mb-6">
            <div className="inline-block bg-blue-900/50 rounded-full p-4 mb-4 border-2 border-blue-700">
              <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Заявка создана!</h2>
            <p className="text-gray-400">№ {displayData.orderId}</p>
          </div>

          {/* Статус заявки */}
          <div className="mb-6 text-center">
            <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold border ${
              displayData.status === 'pending' 
                ? 'bg-yellow-900/50 text-yellow-300 border-yellow-700'
                : displayData.status === 'processing'
                ? 'bg-blue-900/50 text-blue-300 border-blue-700'
                : displayData.status === 'completed'
                ? 'bg-green-900/50 text-green-300 border-green-700'
                : displayData.status === 'cancelled'
                ? 'bg-red-900/50 text-red-300 border-red-700'
                : 'bg-gray-700 text-gray-300'
            }`}>
              {displayData.status === 'pending' ? 'Ожидает' : 
               displayData.status === 'processing' ? 'В обработке' :
               displayData.status === 'completed' ? 'Завершено' :
               displayData.status === 'cancelled' ? 'Отменено' :
               displayData.status}
            </span>
          </div>

          {/* Детали заявки */}
          <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-2xl p-5 mb-6 border border-indigo-800/30">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Тип:</span>
                <span className="font-semibold text-white">{displayData.type === 'alipay' ? 'Alipay' : displayData.type === 'wechat' ? 'WeChat' : displayData.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Сумма:</span>
                <span className="font-semibold text-white">{displayData.amount} CNY</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Способ оплаты:</span>
                <span className="font-semibold text-white">{displayData.paymentMethod}</span>
              </div>
              {displayData.bank && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Банк:</span>
                  <span className="font-semibold text-white">{displayData.bank}</span>
                </div>
              )}
              {displayData.network && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Сеть:</span>
                  <span className="font-semibold text-white">{displayData.network}</span>
                </div>
              )}
            </div>
          </div>

          {/* Для RUB БЕЗ реквизитов - показываем ожидание */}
          {isBank && !hasPaymentDetails && displayData.status !== 'completed' && displayData.status !== 'cancelled' && (
            <div className="bg-blue-900/30 border-2 border-blue-700/50 rounded-2xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <div className="bg-blue-800/50 rounded-full p-2 mt-1">
                  <svg className="w-5 h-5 text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-300 mb-2">⏳ Ожидаем реквизиты</h3>
                  <p className="text-sm text-blue-400 leading-relaxed">
                    В течение <span className="font-bold">15 минут</span> вам будут отправлены банковские реквизиты для оплаты. 
                    Страница автоматически обновится.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Для RUB С реквизитами - показываем реквизиты */}
          {isBank && hasPaymentDetails && (
            <div className="bg-purple-900/30 border-2 border-purple-700/50 rounded-2xl p-5 mb-6">
              <h3 className="font-semibold text-purple-300 mb-3">💳 Реквизиты для оплаты</h3>
              <div className="bg-gray-900/50 rounded-lg p-4 mb-3">
                <pre className="text-white text-sm whitespace-pre-wrap font-mono">
                  {actualPaymentDetails}
                </pre>
              </div>
              <button
                onClick={() => copyToClipboard(actualPaymentDetails, 'Реквизиты')}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Скопировать реквизиты
              </button>
            </div>
          )}

          {/* Реквизиты для TRC-20 */}
          {isTRC20 && usdtToCnyRate && (
            <div className="bg-purple-900/30 border-2 border-purple-700/50 rounded-2xl p-5 mb-6">
              <h3 className="font-semibold text-purple-300 mb-3">Реквизиты для перевода</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-purple-400 font-semibold">Сеть:</label>
                  <div className="bg-gray-900/50 rounded-lg p-3 mt-1">
                    <p className="text-sm font-mono text-white">TRC-20 (TRON)</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-purple-400 font-semibold">Адрес кошелька:</label>
                  <div className="bg-gray-900/50 rounded-lg p-3 mt-1 flex items-center justify-between">
                    <p className="text-xs font-mono text-white break-all">TXvN8rGmkN9qKPvZQVqLjKYq7r8WQJD9eH</p>
                    <button 
                      onClick={() => copyToClipboard('TXvN8rGmkN9qKPvZQVqLjKYq7r8WQJD9eH', 'Адрес')}
                      className="ml-2 text-purple-400 hover:text-purple-300"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-purple-400 font-semibold">Сумма к оплате:</label>
                  <div className="bg-gray-900/50 rounded-lg p-3 mt-1">
                    <p className="text-lg font-bold text-purple-400">
                      {(parseFloat(displayData.amount) / usdtToCnyRate).toFixed(2)} USDT
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-3">
                <p className="text-xs text-yellow-400">
                  <span className="font-semibold">⚠️ Важно:</span> Убедитесь, что выбрана сеть TRC-20. Переводы по другим сетям не будут зачислены!
                </p>
              </div>
            </div>
          )}

          {/* Реквизиты для Bybit */}
          {isBybit && usdtToCnyRate && (
            <div className="bg-orange-900/30 border-2 border-orange-700/50 rounded-2xl p-5 mb-6">
              <h3 className="font-semibold text-orange-300 mb-3">Реквизиты для перевода</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-orange-400 font-semibold">Платформа:</label>
                  <div className="bg-gray-900/50 rounded-lg p-3 mt-1">
                    <p className="text-sm font-mono text-white">Bybit (Внутренний перевод)</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-orange-400 font-semibold">UID или Email:</label>
                  <div className="bg-gray-900/50 rounded-lg p-3 mt-1 flex items-center justify-between">
                    <p className="text-sm font-mono text-white">12345678</p>
                    <button 
                      onClick={() => copyToClipboard('12345678', 'UID')}
                      className="ml-2 text-orange-400 hover:text-orange-300"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-orange-400 font-semibold">Сумма к оплате:</label>
                  <div className="bg-gray-900/50 rounded-lg p-3 mt-1">
                    <p className="text-lg font-bold text-orange-400">
                      {(parseFloat(displayData.amount) / usdtToCnyRate).toFixed(2)} USDT
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-blue-900/30 border border-blue-700/50 rounded-lg p-3">
                <p className="text-xs text-blue-400">
                  <span className="font-semibold">💡 Как отправить:</span> Зайдите в Bybit → Активы → Внутренний перевод → Введите UID и сумму
                </p>
              </div>
            </div>
          )}

          {/* Индикатор автообновления */}
          {displayData.status !== 'completed' && displayData.status !== 'cancelled' && (
            <div className="mb-6 text-center text-gray-500 text-sm">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Автоматическое обновление каждые 5 секунд</span>
              </div>
            </div>
          )}

          {/* Инструкции */}
          <div className="bg-gray-900/50 rounded-2xl p-5 mb-6 border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Что дальше?</h3>
            <ol className="space-y-2 text-sm text-gray-300">
              <li className="flex items-start gap-2">
                <span className="font-bold text-indigo-400 min-w-[20px]">1.</span>
                <span>{isBank && !hasPaymentDetails ? 'Дождитесь реквизитов в течение 15 минут' : 'Отправьте средства по указанным реквизитам'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-indigo-400 min-w-[20px]">2.</span>
                <span>{isBank ? 'Переведите средства на полученные реквизиты' : 'Отправьте скриншот транзакции оператору'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-indigo-400 min-w-[20px]">3.</span>
                <span>Дождитесь подтверждения от оператора</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-indigo-400 min-w-[20px]">4.</span>
                <span>Средства поступят на ваш {displayData.type === 'alipay' ? 'Alipay' : displayData.type === 'wechat' ? 'WeChat' : displayData.type} аккаунт</span>
              </li>
            </ol>
          </div>

          {/* Кнопки */}
          <div className="space-y-3">
            <button
              onClick={() => setCurrentScreen('support')}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl py-4 font-semibold shadow-lg hover:shadow-xl hover:from-indigo-500 hover:to-purple-500 transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Связаться с поддержкой
            </button>
            <button
              onClick={() => {
                setCurrentScreen('main');
                setOrderDetails(null);
              }}
              className="w-full bg-gray-700 text-gray-200 rounded-xl py-4 font-semibold hover:bg-gray-600 transition-all border border-gray-600"
            >
              Вернуться на главную
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationScreen;
